import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

const TAABIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/ed75ipfo_Ta%C3%A0biy.png';
const YEEBIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/49uszg6d_Ye%C3%A8biy.png';

const STORAGE_KEY = 'lamfon_walkthrough_completed';

const WALKTHROUGH_STEPS = [
  {
    target: null, // Full-screen welcome
    title: "Welcome to LámFon!",
    body: "Let me show you around. This quick tour will help you get the most out of the app.",
    assistant: 'taabiy',
    position: 'center'
  },
  {
    target: '[data-testid="home-search"]',
    title: "Search & Translate",
    body: "Search any word in Lamnso' or English. The app has over 13,000 translations. Try typing a word like \"water\" or \"love\".",
    assistant: 'yeebiy',
    position: 'below'
  },
  {
    target: '[data-testid="toggle-keyboard-button"], [data-testid="community-keyboard-toggle"]',
    title: "Lamnso' Keyboard",
    body: "Tap the əŋ button to open the special Lamnso' keyboard. It has ə (schwa) and ŋ (eng) built right in, plus accent marks when you long-press vowels.",
    assistant: 'taabiy',
    position: 'above'
  },
  {
    target: '[data-testid="home-learn-card"]',
    title: "Learn Lamnso'",
    body: "Structured lessons teach you the alphabet, special characters, tones, and word patterns. Complete quizzes to earn XP and level up!",
    assistant: 'yeebiy',
    position: 'below'
  },
  {
    target: '[data-testid="home-practice-card"]',
    title: "Practice Reading",
    body: "Practice with real bilingual sentences from literature. Read in Lamnso', then reveal the English translation to check your understanding.",
    assistant: 'taabiy',
    position: 'below'
  },
  {
    target: '[data-testid="bottomnav-community"], [data-testid="home-community-card"]',
    title: "Join the Community",
    body: "Ask questions, share proverbs, post tips, and learn from Wirfon (Elders). You can also suggest translations for words that haven't been added yet.",
    assistant: 'yeebiy',
    position: 'above'
  },
  {
    target: '[data-testid="bottomnav-profile"]',
    title: "Your Profile",
    body: "Track your XP, streak, and level progress. You'll advance from Wán (Beginner) through Wà'bìn (Speaker) and Ránsísí (Storyteller) as you learn.",
    assistant: 'taabiy',
    position: 'above'
  },
  {
    target: null,
    title: "You're all set!",
    body: "Start by searching a word or jumping into your first lesson. Every search, vote, and lesson completed helps preserve the Lamnso' language. Enjoy!",
    assistant: 'both',
    position: 'center'
  }
];

const ELDER_EXTRA_STEPS = [
  {
    target: '[data-testid="home-validate-card"]',
    title: "Wirfon Review",
    body: "As a Wirfon, you can validate translations — confirm correct ones, reject incorrect ones, or suggest alternatives. Your reviews carry 3× the weight of regular votes.",
    assistant: 'taabiy',
    position: 'below',
    insertAfter: 5
  },
  {
    target: '[data-testid="home-admin-card"]',
    title: "Admin Dashboard",
    body: "Access the admin panel to manage users, upload data, review statistics, and control community bots and banners.",
    assistant: 'yeebiy',
    position: 'below',
    insertAfter: 6
  }
];

export default function Walkthrough({ user, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    // Build step list based on user level
    let allSteps = [...WALKTHROUGH_STEPS];
    if (user?.level >= 4 || user?.role === 'admin') {
      // Insert elder steps at the right positions
      for (const extra of ELDER_EXTRA_STEPS) {
        const insertIdx = extra.insertAfter + 1;
        allSteps.splice(Math.min(insertIdx, allSteps.length - 1), 0, extra);
      }
    }
    setSteps(allSteps);
  }, [user]);

  const updateTargetRect = useCallback(() => {
    if (!steps.length) return;
    const step = steps[currentStep];
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    // Try each selector (comma-separated)
    const selectors = step.target.split(',').map(s => s.trim());
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect(rect);
          // Scroll into view
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    }
    setTargetRect(null);
  }, [currentStep, steps]);

  useEffect(() => {
    updateTargetRect();
    const timer = setTimeout(updateTargetRect, 300);
    window.addEventListener('resize', updateTargetRect);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [updateTargetRect]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleComplete = () => {
    if (user?.user_id) {
      try {
        const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        data[user.user_id] = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) { /* */ }
    }
    onComplete();
  };

  if (!steps.length) return null;

  const step = steps[currentStep];
  const isCenter = !targetRect || step.position === 'center';
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Calculate tooltip position
  let tooltipStyle = {};
  if (targetRect && !isCenter) {
    const padding = 12;
    if (step.position === 'below') {
      tooltipStyle = {
        top: targetRect.bottom + padding,
        left: Math.max(12, Math.min(targetRect.left, window.innerWidth - 320)),
      };
    } else {
      tooltipStyle = {
        top: Math.max(12, targetRect.top - padding - 240),
        left: Math.max(12, Math.min(targetRect.left, window.innerWidth - 320)),
      };
    }
  }

  const assistantImg = step.assistant === 'both'
    ? null
    : step.assistant === 'yeebiy' ? YEEBIY : TAABIY;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="walkthrough">
      {/* Overlay with cutout for target */}
      <div className="absolute inset-0" onClick={handleNext}>
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <mask id="walkthrough-mask">
              <rect width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8} y={targetRect.top - 8}
                  width={targetRect.width + 16} height={targetRect.height + 16}
                  rx="12" fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#walkthrough-mask)" />
        </svg>
      </div>

      {/* Highlight ring around target */}
      {targetRect && (
        <div className="absolute pointer-events-none animate-pulse"
          style={{
            top: targetRect.top - 8, left: targetRect.left - 8,
            width: targetRect.width + 16, height: targetRect.height + 16,
            border: '2px solid #4CAF50', borderRadius: '12px',
            boxShadow: '0 0 0 4px rgba(76,175,80,0.3)'
          }}
        />
      )}

      {/* Skip button */}
      <button onClick={handleComplete}
        className="absolute top-4 right-4 z-10 flex items-center gap-1 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-xs font-semibold text-[#57534E] hover:text-[#1C1917] shadow-lg"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        data-testid="skip-walkthrough">
        Skip tour <X className="w-3 h-3" />
      </button>

      {/* Tooltip card */}
      <div className={`absolute z-10 ${isCenter ? 'inset-0 flex items-center justify-center p-6' : ''}`}
        style={isCenter ? {} : { ...tooltipStyle, position: 'fixed' }}
        onClick={(e) => e.stopPropagation()}>
        <div className={`bg-white rounded-2xl shadow-2xl ${isCenter ? 'max-w-sm w-full' : 'w-[300px]'} overflow-hidden`}
          data-testid="walkthrough-tooltip">
          {/* Progress bar */}
          <div className="h-1 bg-[#E7E5E4]">
            <div className="h-full bg-[#4CAF50] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>

          <div className="p-5">
            {/* Assistant */}
            <div className="flex items-start gap-3 mb-3">
              {step.assistant === 'both' ? (
                <div className="flex -space-x-2 flex-shrink-0">
                  <img src={TAABIY} alt="Taàbiy" className="w-10 h-10 object-contain animate-bounce-gentle" />
                  <img src={YEEBIY} alt="Yeèbiy" className="w-10 h-10 object-contain animate-bounce-gentle" style={{ animationDelay: '0.2s' }} />
                </div>
              ) : (
                <img src={assistantImg} alt="" className="w-10 h-10 object-contain flex-shrink-0 animate-bounce-gentle" />
              )}
              <div>
                <h3 className="text-base font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {step.title}
                </h3>
              </div>
            </div>

            <p className="text-sm text-[#57534E] leading-relaxed mb-4">
              {step.body}
            </p>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {steps.map((_, i) => (
                  <div key={`step-dot-${i}`} className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === currentStep ? 'bg-[#4CAF50]' : i < currentStep ? 'bg-[#4CAF50]/40' : 'bg-[#E7E5E4]'
                  }`} />
                ))}
              </div>

              <div className="flex gap-2">
                {!isFirst && (
                  <button onClick={handlePrev}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-[#57534E] hover:text-[#1C1917] rounded-xl"
                    data-testid="walkthrough-prev">
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                  </button>
                )}
                <button onClick={handleNext}
                  className="flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white bg-[#4CAF50] hover:bg-[#3D8B40] rounded-xl shadow-sm"
                  data-testid="walkthrough-next">
                  {isLast ? "Let's go!" : 'Next'} {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldShowWalkthrough(userId) {
  if (!userId) return false;
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return !data[userId];
  } catch {
    return true;
  }
}
